import React, { useEffect, useRef } from 'react';

/**
 * DaySelector component for selecting acceptable days of the week
 * Used in the AutoScheduler for filtering schedule suggestions
 */
function DaySelector({ activeDays, selectedDays, onChange }) {
    const buttonRefs = useRef({});

    // Handle keyboard navigation
    const handleKeyDown = (event, day) => {
        const currentIndex = activeDays.indexOf(day);
        let targetIndex = -1;

        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                targetIndex = currentIndex > 0 ? currentIndex - 1 : activeDays.length - 1;
                break;
            case 'ArrowRight':
                event.preventDefault();
                targetIndex = currentIndex < activeDays.length - 1 ? currentIndex + 1 : 0;
                break;
            case 'Home':
                event.preventDefault();
                targetIndex = 0;
                break;
            case 'End':
                event.preventDefault();
                targetIndex = activeDays.length - 1;
                break;
            case ' ':
            case 'Enter':
                event.preventDefault();
                handleDayToggle(day);
                return;
            default:
                return;
        }

        if (targetIndex >= 0 && targetIndex < activeDays.length) {
            const targetDay = activeDays[targetIndex];
            buttonRefs.current[targetDay]?.focus();
        }
    };

    // Handle day selection toggle
    const handleDayToggle = (day) => {
        const newSelectedDays = selectedDays.includes(day)
            ? selectedDays.filter(d => d !== day)
            : [...selectedDays, day];

        onChange(newSelectedDays);
    };

    // Capitalize first letter of day name
    const capitalizeDay = (day) => {
        return day.charAt(0).toUpperCase() + day.slice(1);
    };

    // Get abbreviated day name for display
    const getAbbreviatedDay = (day) => {
        const abbreviations = {
            monday: 'Mon',
            tuesday: 'Tue',
            wednesday: 'Wed',
            thursday: 'Thu',
            friday: 'Fri',
            saturday: 'Sat',
            sunday: 'Sun'
        };
        return abbreviations[day.toLowerCase()] || capitalizeDay(day);
    };

    return (
        <div className="day-selector">
            <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Select days of the week"
            >
                {activeDays.map((day, index) => {
                    const isSelected = selectedDays.includes(day);
                    const abbreviatedDay = getAbbreviatedDay(day);
                    const fullDay = capitalizeDay(day);

                    return (
                        <button
                            key={day}
                            ref={el => buttonRefs.current[day] = el}
                            type="button"
                            className={`
                px-3 py-2 min-w-[3rem] text-sm font-medium rounded-md
                border-2 transition-all duration-150 ease-in-out
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${isSelected
                                    ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
                            onClick={() => handleDayToggle(day)}
                            onKeyDown={(e) => handleKeyDown(e, day)}
                            aria-pressed={isSelected}
                            aria-label={`${isSelected ? 'Deselect' : 'Select'} ${fullDay}`}
                            title={`${isSelected ? 'Deselect' : 'Select'} ${fullDay}`}
                            tabIndex={index === 0 ? 0 : -1}
                        >
                            <span className="block sm:hidden" aria-hidden="true">
                                {abbreviatedDay}
                            </span>
                            <span className="hidden sm:block" aria-hidden="true">
                                {fullDay}
                            </span>
                            <span className="sr-only">
                                {fullDay} {isSelected ? '(selected)' : '(not selected)'}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Helper text */}
            <div className="mt-2 text-sm text-gray-600">
                {selectedDays.length === 0 && (
                    <span className="text-red-600" role="alert">
                        Please select at least one day
                    </span>
                )}
                {selectedDays.length > 0 && (
                    <span>
                        {selectedDays.length === activeDays.length
                            ? 'All active days selected'
                            : `${selectedDays.length} of ${activeDays.length} days selected`
                        }
                    </span>
                )}
            </div>
        </div>
    );
}

export default DaySelector;